import { useEffect, useMemo, useState } from "react";
import { Search, Copy, Terminal, Crown } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { Reveal } from "../components/motion";

const CATEGORIES = ["All", "Music", "Queue", "Playlists", "Groove AI", "Utility"];

export default function Commands() {
  const [commands, setCommands] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/commands")
      .then((r) => setCommands(r.data))
      .catch(() => toast.error("Could not load commands"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return commands.filter((c) =>
      (category === "All" || c.category === category) &&
      (!q || c.name.includes(q) || c.description.toLowerCase().includes(q) || (c.aliases || []).some((a) => a.includes(q)))
    );
  }, [commands, query, category]);

  const copy = (usage) => {
    navigator.clipboard.writeText(usage);
    toast.success(`Copied ${usage}`);
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="commands-page">
      <section className="hero-glow border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.35em] text-volt font-semibold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-volt inline-block" /> Reference
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight" style={{ fontWeight: 900 }} data-testid="commands-headline">
              COMMAND <span className="text-outline-blurple">DECK</span>
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mt-6 max-w-xl">
              {commands.length} commands, one prefix. Search, tap to copy, paste into Discord.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative max-w-xl mt-10">
              <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search commands… try 'queue' or 'play'"
                data-testid="commands-search-input"
                className="w-full bg-white/5 border border-white/15 rounded-full pl-12 pr-5 py-4 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blurple focus:border-transparent"
              />
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="flex flex-wrap gap-2 mt-6" data-testid="commands-categories">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-5 py-2 rounded-full text-sm font-semibold font-heading transition-colors duration-200 ${
                    category === c ? "bg-blurple text-white" : "border border-white/15 text-white/60 hover:text-white hover:border-white/40"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {loading ? (
          <p className="text-white/40 font-mono text-sm" data-testid="commands-loading">Loading commands…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24" data-testid="commands-empty">
            <Terminal size={36} className="mx-auto text-white/20 mb-4" />
            <p className="text-white/50">No commands match “{query}”.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="commands-list">
            {filtered.map((c, i) => {
              const usage = c.usage || `!${c.name}`;
              return (
                <Reveal key={c.id} delay={(i % 4) * 0.04}>
                  <div className="group rounded-xl border border-white/10 bg-white/[0.03] hover:border-blurple/40 p-6 transition-colors duration-200" data-testid={`command-${c.name.replace(/\s+/g, "-")}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <code className="font-mono text-volt font-semibold text-base">!{c.name}</code>
                          {c.premium && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-400 border border-amber-400/30 rounded-full px-2 py-0.5">
                              <Crown size={10} /> Premium
                            </span>
                          )}
                          <span className="text-[10px] font-mono uppercase tracking-wider text-white/30 border border-white/10 rounded-full px-2 py-0.5">
                            {c.category}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2.5 leading-relaxed">{c.description}</p>
                        {c.aliases?.length > 0 && (
                          <p className="font-mono text-xs text-white/35 mt-2">aka {c.aliases.map((a) => `!${a}`).join(", ")}</p>
                        )}
                      </div>
                      <button
                        onClick={() => copy(usage)}
                        data-testid={`copy-${c.name.replace(/\s+/g, "-")}`}
                        className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-white/50 hover:text-white border border-white/10 hover:border-blurple/50 hover:bg-blurple/10 rounded-full px-3.5 py-2 transition-colors duration-200"
                        aria-label={`Copy ${usage}`}
                      >
                        <Copy size={13} /> Copy
                      </button>
                    </div>
                    <div className="mt-4 bg-black/50 border border-white/5 rounded-lg px-4 py-2.5 scanlines">
                      <code className="font-mono text-xs text-emerald-400/90">{usage}</code>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
